import { useState } from "react";
import Page from "../../../../components/Layout/Page";
import { useIsMobile } from "../../../../helpers/ui";
import { colors } from "../../../../styles";
import { DesktopHeader, MobileHeader } from "../Account/components";
import {
  employeeAccountPath,
  employeeDebitCardInfoPath,
} from "../../../../routes/paths";
import HeaderBreadcrumbs from "../../../../components/HeaderBreadcrumbs";
import T from "../../../../components/Typography";
import ResponsePageContent from "../../../../components/Layout/ResponsivePageContent";
import { useNavigate } from "react-router-dom";
import { SectionFlexColumn, TextInput } from "../../../../components/General";
import { ReactComponent as DebitCardBack } from "../../../../assets/images/debitcard-back.svg";
import Button from "../../../../components/Button";
import useConfirmDebitLastFour from "../../../../hooks/mutations/employee/useConfirmDebitLastFour";
import useGetAccount from "../../../../hooks/queries/employee/useGetAccount";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import useSetCardPin from "../../../../hooks/mutations/employee/useSetCardPin";
import Loader from "../../../../components/Loader";

const ActivateCard = () => {
  const [step, setStep] = useState<"last4" | "pin">("last4");
  const [last4, setLast4] = useState("");
  const [pin, setPin] = useState("");
  const { data: accountData } = useGetAccount();
  const { mutateAsync: debitCheckLast4, status: confirmLast4Status } =
    useConfirmDebitLastFour();
  const { mutateAsync: activateCard, status: activateCardStatus } =
    useSetCardPin();

  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const title = "Activate Debit Card";
  const subtitle = "Get started with your card";
  const breadcrumbs = ["My Account", title];

  const doBack = () => {
    navigate(employeeAccountPath);
  };

  const confirmLast4 = async () => {
    try {
      const cardId = accountData?.challenger_saving.debit_card.latest_card_id;
      if (cardId) {
        const res = await debitCheckLast4({ cardId, last4 });
        if (!res.error) {
          setStep("pin");
        }
      }
    } catch (error) {
      const err = error as AxiosError;
      if (err.response && err.response.data) {
        // @ts-ignore
        toast.error(err.response.data.detail);
      }
    }
  };
  const confirmPin = async () => {
    try {
      const cardId = accountData?.challenger_saving.debit_card.latest_card_id;
      if (cardId && pin.length === 4) {
        const response = await activateCard({ cardId, last4, pin });
        if (!response.err) {
          toast.success("Card activated successfully!");
          navigate(employeeDebitCardInfoPath);
          return;
        }
      }
    } catch (error) {
      toast.error("Error when activating card");
    }
  };

  if (confirmLast4Status === "loading" || activateCardStatus === "loading") {
    return <Loader />;
  }

  const description =
    step === "last4"
      ? "Once your debit card arrives in the mail, enter the last four digits to activate."
      : "Set a four digit pin for your debit card.";

  return (
    <Page>
      {isMobile ? (
        <MobileHeader
          title={title}
          showBack
          showClose
          handleBack={doBack}
          closeRoutePath={employeeAccountPath}
        />
      ) : (
        <DesktopHeader>
          <HeaderBreadcrumbs breadcrumbs={breadcrumbs} handleBack={doBack} />
        </DesktopHeader>
      )}
      <ResponsePageContent title={title} subtitle={subtitle}>
        <SectionFlexColumn sx={{ alignItems: "center", padding: "8px 0" }}>
          <div
            style={{ padding: "16px", backgroundColor: colors.gray_100 }}
            className="flex items-center justify-center w-100"
          >
            <DebitCardBack />
          </div>
          <div style={{ width: isMobile ? "100%" : "50%" }}>
            <T
              variant="mdReg"
              color={colors.gray_600}
              textAlign="center"
              marginTop={"16px"}
              marginBottom={"16px"}
            >
              {description}
            </T>
            <SectionFlexColumn>
              {step === "last4" && (
                <>
                  <TextInput
                    size="small"
                    placeholder="Last 4 Digits"
                    sx={{ marginBottom: "8x" }}
                    value={last4}
                    onChange={(e) => {
                      if (e.target.value.length > 4) return;
                      setLast4(e.target.value);
                    }}
                  />
                  <Button
                    noMargin
                    sx={{ marginTop: "16px" }}
                    disabled={last4.length < 4}
                    onClick={confirmLast4}
                  >
                    Confirm
                  </Button>
                </>
              )}
              {step === "pin" && (
                <>
                  <TextInput
                    size="small"
                    placeholder="Set four digit pin"
                    sx={{ marginBottom: "8x" }}
                    value={pin}
                    onChange={(e) => {
                      if (e.target.value.length > 4) return;
                      setPin(e.target.value);
                    }}
                  />
                  <Button
                    noMargin
                    sx={{ marginTop: "16px" }}
                    disabled={pin.length < 4}
                    onClick={confirmPin}
                  >
                    Activate Card
                  </Button>
                </>
              )}
            </SectionFlexColumn>
          </div>
        </SectionFlexColumn>
      </ResponsePageContent>
    </Page>
  );
};

export default ActivateCard;
